import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"

import { groupBy } from "shared/lib/arrayUtils"
import { useDebounce } from "shared/lib/performance/useDebounce"
import { useEvent } from "shared/lib/useEvent"
import {
  CreateFollowUpConfigurationInput,
  UpdateFollowUpConfigurationInput
} from "shared/types/__generated__/graphql"

import { zodResolver } from "@hookform/resolvers/zod"
import { FollowUpConfiguration } from "entities/dim-configuration/types/FollowUpConfiguration"
import I18n from "i18n"
import z from "zod"

import { FollowUpConfigurationGroupData } from "../types/FollowUpConfigurationGroupData"

export interface NewEntryFormData {
  sourceProductName: string | null
  targetProductName: string | null
}

export interface GroupEntryInput {
  targetProductName: string
}

const newEntrySchema = z.object({
  sourceProductName: z.string().min(1, I18n.t("live:Validation.required")),
  targetProductName: z.string().min(1, I18n.t("live:Validation.required"))
})

interface UseFollowUpConfigurationAdminPageViewModelProps {
  followUpConfigurations: FollowUpConfiguration[]
  createConfiguration: (
    input: CreateFollowUpConfigurationInput
  ) => Promise<void>
  updateConfiguration: (
    id: string,
    input: UpdateFollowUpConfigurationInput
  ) => Promise<void>
  deleteConfiguration: (id: string) => Promise<void>
}

function matchesFilter(entry: FollowUpConfiguration, filter: string) {
  return [
    entry.sourceProduct.productName,
    entry.targetProduct.productName
  ].some(value => value.toLowerCase().includes(filter))
}

function byPosition(a: FollowUpConfiguration, b: FollowUpConfiguration) {
  return a.position - b.position
}

function groupBySourceProduct(
  followUpConfigurations: FollowUpConfiguration[]
): FollowUpConfigurationGroupData[] {
  const groups = groupBy(
    followUpConfigurations,
    entry => entry.sourceProduct.id
  )

  return Object.values(groups)
    .map(entries => ({
      sourceProduct: entries[0].sourceProduct,
      entries: [...entries].sort(byPosition)
    }))
    .sort((a, b) =>
      a.sourceProduct.productName.localeCompare(b.sourceProduct.productName)
    )
}

export function useFollowUpConfigurationAdminPageViewModel({
  followUpConfigurations,
  createConfiguration,
  updateConfiguration,
  deleteConfiguration
}: UseFollowUpConfigurationAdminPageViewModelProps) {
  const [filterText, setFilterText] = useState("")
  const debouncedFilterText = useDebounce(filterText, 500)
  const isReorderDisabled = filterText.trim().length > 0

  const newEntryForm = useForm<NewEntryFormData>({
    resolver: zodResolver<
      NewEntryFormData,
      unknown,
      z.infer<typeof newEntrySchema>
    >(newEntrySchema),
    defaultValues: {
      sourceProductName: null,
      targetProductName: null
    }
  })

  const orderedGroups = useMemo(
    () => groupBySourceProduct(followUpConfigurations),
    [followUpConfigurations]
  )

  const groups = useMemo(() => {
    const filter = debouncedFilterText.trim().toLowerCase()
    if (!filter) return orderedGroups

    return groupBySourceProduct(
      followUpConfigurations.filter(entry => matchesFilter(entry, filter))
    )
  }, [followUpConfigurations, orderedGroups, debouncedFilterText])

  const handleUpdateRow = useEvent(
    (id: string, input: UpdateFollowUpConfigurationInput) =>
      updateConfiguration(id, input)
  )

  const handleDelete = useEvent((id: string) => deleteConfiguration(id))

  const nextPosition = useEvent((sourceProductName: string) => {
    const group = orderedGroups.find(
      candidate => candidate.sourceProduct.productName === sourceProductName
    )
    if (!group) return 0

    return Math.max(...group.entries.map(entry => entry.position)) + 1
  })

  const handleAddToGroup = useEvent(
    (sourceProductName: string, data: GroupEntryInput) =>
      createConfiguration({
        sourceProductName,
        targetProductName: data.targetProductName,
        position: nextPosition(sourceProductName),
        isActive: true
      })
  )

  const handleAddEntry = useEvent(
    newEntryForm.handleSubmit(async data => {
      if (!data.sourceProductName || !data.targetProductName) return

      await createConfiguration({
        sourceProductName: data.sourceProductName,
        targetProductName: data.targetProductName,
        position: nextPosition(data.sourceProductName),
        isActive: true
      })

      newEntryForm.reset({
        sourceProductName: data.sourceProductName,
        targetProductName: null
      })
    })
  )

  return {
    groups,
    filterText,
    setFilterText,
    isReorderDisabled,
    newEntryForm,
    handleUpdateRow,
    handleDelete,
    handleAddToGroup,
    handleAddEntry
  }
}
